import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ref, onValue, query, orderByChild, equalTo } from 'firebase/database';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Copy,
  CreditCard,
  ExternalLink,
  FileText,
  Gamepad2,
  Gift,
  Loader2,
  Plus,
  ReceiptText,
  RefreshCw,
  Sparkles,
  XCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { database } from '../services/firebase';
import { DB_PATHS } from '../services/schema';
import { getAuthRedirectPath } from '../utils/authRedirect';
import RequestScriptModal from '../components/modals/RequestScriptModal';

const statusConfig = {
  payment_pending: {
    label: 'Payment pending',
    shortLabel: 'Pending pay',
    icon: CreditCard,
    className: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300'
  },
  pending: {
    label: 'Verification',
    shortLabel: 'Verify',
    icon: Clock,
    className: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300'
  },
  accepted: {
    label: 'Accepted',
    shortLabel: 'Accepted',
    icon: CheckCircle2,
    className: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300'
  },
  approved: {
    label: 'Approved',
    shortLabel: 'Approved',
    icon: Gift,
    className: 'border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-500/20 dark:bg-primary-500/10 dark:text-primary-300'
  },
  refunded: {
    label: 'Refunded',
    shortLabel: 'Refunded',
    icon: Gift,
    className: 'border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-500/20 dark:bg-purple-500/10 dark:text-purple-300'
  },
  denied: {
    label: 'Denied',
    shortLabel: 'Denied',
    icon: XCircle,
    className: 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300'
  }
};

const normalizeStatus = (status) => {
  const value = String(status || 'pending').toLowerCase();
  if (value === 'rejected' || value === 'payment_rejected' || value === 'payment_denied') return 'denied';
  if (value === 'verified') return 'accepted';
  if (value === 'completed') return 'approved';
  return statusConfig[value] ? value : 'pending';
};

const toTimestamp = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value > 1e11 ? value : value * 1000;
  const parsed = Date.parse(String(value || ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDate = (value) => {
  const ts = toTimestamp(value);
  if (!ts) return 'Recently';
  return new Date(ts).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const copyText = async (value) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const RequestCard = ({ request }) => {
  const status = normalizeStatus(request.status);
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;
  const approvedProductLink = request.approvedProductId ? `/product/${request.approvedProductId}` : '';

  const handleCopyCoupon = async () => {
    if (!request.couponCode) return;
    try {
      await copyText(request.couponCode);
      toast.success('Coupon code copied');
    } catch {
      toast.error('Failed to copy coupon');
    }
  };

  return (
    <article className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-start gap-3 p-3 sm:p-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
          <Gamepad2 className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="truncate text-sm font-black text-gray-950 dark:text-white sm:text-base">{request.gameName || 'Requested Script'}</h3>
              <p className="mt-0.5 text-xs font-semibold text-gray-500 dark:text-gray-400">{formatDate(request.createdAt)}</p>
            </div>
            <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-black uppercase ${config.className}`}>
              <Icon className="h-3 w-3" />
              {config.shortLabel}
            </span>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-1.5 rounded-2xl bg-gray-50 p-1.5 dark:bg-gray-950/40">
            <div className="min-w-0 rounded-xl bg-white px-2 py-1.5 dark:bg-gray-900">
              <p className="text-[9px] font-black uppercase text-gray-400">Fee paid</p>
              <p className="truncate text-xs font-black text-gray-950 dark:text-white">
                ${Number(request.requestFee ?? 5).toFixed(2)}
              </p>
            </div>
            <div className="min-w-0 rounded-xl bg-white px-2 py-1.5 dark:bg-gray-900">
              <p className="text-[9px] font-black uppercase text-gray-400">Pledged</p>
              <p className="truncate text-xs font-black text-emerald-600 dark:text-emerald-400">
                ${Number(request.offerAmount ?? request.requestFee ?? 5).toFixed(2)}
              </p>
            </div>
            <div className="min-w-0 rounded-xl bg-white px-2 py-1.5 dark:bg-gray-900">
              <p className="text-[9px] font-black uppercase text-gray-400">Method</p>
              <p className="truncate text-xs font-black text-gray-950 dark:text-white">
                {request.paidWithPoints ? `${request.pointsSpent || 0} pts` : (request.paymentMethod || 'Submitted')}
              </p>
            </div>
          </div>

          {(request.storeLink || approvedProductLink) && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {request.storeLink && (
                <a
                  href={request.storeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-8 items-center gap-1.5 rounded-xl bg-blue-50 px-2.5 text-xs font-black text-blue-700 transition hover:bg-blue-100 dark:bg-blue-500/10 dark:text-blue-300 dark:hover:bg-blue-500/20"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Play Store
                </a>
              )}
              {approvedProductLink && (
                <a
                  href={approvedProductLink}
                  className="inline-flex min-h-8 items-center gap-1.5 rounded-xl bg-primary-50 px-2.5 text-xs font-black text-primary-700 transition hover:bg-primary-100 dark:bg-primary-500/10 dark:text-primary-300 dark:hover:bg-primary-500/20"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Store Product
                </a>
              )}
            </div>
          )}

          {request.note && (
            <div className="mt-2 flex gap-2 rounded-2xl border border-gray-100 bg-gray-50 p-2 dark:border-gray-800 dark:bg-gray-950/40">
              <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
              <p className="line-clamp-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300">{request.note}</p>
            </div>
          )}

          {request.couponCode && (
            <div className="mt-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-2.5 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[9px] font-black uppercase tracking-wider opacity-80">Coupon issued</p>
                  <p className="mt-0.5 truncate font-mono text-xs font-black">{request.couponCode}</p>
                  {request.approvedProductName && (
                    <p className="mt-0.5 truncate text-[11px] font-semibold opacity-80">{request.approvedProductName}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleCopyCoupon}
                  className="inline-flex min-h-8 shrink-0 items-center justify-center gap-1.5 rounded-xl bg-white px-2.5 text-xs font-black text-emerald-700 shadow-sm transition hover:bg-emerald-50 dark:bg-gray-950/50 dark:text-emerald-200 dark:hover:bg-gray-950"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy
                </button>
              </div>
            </div>
          )}

          {request.refundPoints && (
            <div className="mt-2 rounded-2xl border border-purple-200 bg-purple-50 px-2.5 py-2 text-xs font-black text-purple-700 dark:border-purple-500/20 dark:bg-purple-500/10 dark:text-purple-300">
              Refunded: {request.refundPoints} PSH points
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

const ScriptRequests = () => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestModalOpen, setRequestModalOpen] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setRequests([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const requestsQuery = query(ref(database, DB_PATHS.SCRIPT_REQUESTS), orderByChild('userId'), equalTo(user.uid));
    const unsubscribe = onValue(
      requestsQuery,
      (snapshot) => {
        const data = snapshot.val() || {};
        const nextRequests = Object.entries(data)
          .map(([id, value]) => ({ id, ...(value || {}) }))
          .sort((a, b) => toTimestamp(b.createdAt || b.updatedAt) - toTimestamp(a.createdAt || a.updatedAt));
        setRequests(nextRequests);
        setLoading(false);
      },
      () => {
        setRequests([]);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [user?.uid]);

  const counts = useMemo(() => requests.reduce((acc, request) => {
    const status = normalizeStatus(request.status);
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {}), [requests]);

  if (!isAuthenticated) {
    return <Navigate to={getAuthRedirectPath('/script-requests')} replace />;
  }

  const statItems = [
    ['pending', 'Verify'],
    ['accepted', 'Accepted'],
    ['approved', 'Approved'],
    ['refunded', 'Refunded']
  ];

  return (
    <div className="min-h-screen bg-gray-50/80 py-3 dark:bg-gray-950 sm:py-5">
      <div className="container mx-auto max-w-4xl px-3 sm:px-4">
        <section className="mb-3 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="relative p-3 sm:p-4">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary-500/10 via-transparent to-emerald-400/10 dark:from-primary-500/15 dark:to-emerald-400/10" />
            <div className="relative flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-300">
                <ReceiptText className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-0.5 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                  <Sparkles className="h-3 w-3" />
                  Paid requests
                </div>
                <h1 className="truncate text-xl font-black leading-tight text-gray-950 dark:text-white sm:text-2xl">Script Requests</h1>
                <p className="mt-0.5 line-clamp-1 text-xs font-semibold text-gray-500 dark:text-gray-400">Track script requests, coupons, and refunds.</p>
              </div>
              <button
                type="button"
                onClick={() => setRequestModalOpen(true)}
                className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-2xl bg-primary-600 px-3 text-xs font-black text-white shadow-sm transition hover:bg-primary-700 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-100 sm:px-4"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Request</span>
              </button>
            </div>
          </div>
        </section>

        <div className="mb-3 grid grid-cols-4 gap-1.5">
          {statItems.map(([key, label]) => (
            <div key={key} className="rounded-2xl border border-gray-100 bg-white px-2 py-2 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <p className="truncate text-[9px] font-black uppercase text-gray-400">{label}</p>
              <p className="mt-0.5 text-lg font-black text-gray-950 dark:text-white">{counts[key] || 0}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center rounded-2xl border border-gray-100 bg-white py-10 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
            <span className="ml-2 text-sm font-black text-gray-600 dark:text-gray-300">Loading...</span>
          </div>
        ) : requests.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-6 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-300">
              <AlertCircle className="h-6 w-6" />
            </div>
            <h2 className="text-base font-black text-gray-950 dark:text-white">No requests yet</h2>
            <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-gray-500 dark:text-gray-400">
              Complete payment proof and your request will appear here.
            </p>
            <button
              type="button"
              onClick={() => setRequestModalOpen(true)}
              className="mt-4 inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-primary-600 px-4 text-xs font-black text-white transition hover:bg-primary-700"
            >
              <Plus className="h-4 w-4" />
              Request New
            </button>
          </div>
        ) : (
          <div className="grid gap-2.5 lg:grid-cols-2">
            {requests.map((request) => (
              <RequestCard key={request.id} request={request} />
            ))}
          </div>
        )}

        <div className="mt-3 flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-2.5 text-xs font-bold leading-relaxed text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p>Higher offer = higher and faster chance of the script being built.</p>
        </div>

        <div className="mt-2 flex items-start gap-2 rounded-2xl border border-blue-200 bg-blue-50 p-2.5 text-xs leading-relaxed text-blue-800 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-200">
          <RefreshCw className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p>The request fee is charged now to submit. Your pledged offer is only paid if the script gets built for you.</p>
        </div>
      </div>

      <RequestScriptModal
        isOpen={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
      />
    </div>
  );
};

export default ScriptRequests;
